import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './lib/store';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';

const LandingPage = lazy(() => import('./pages/LandingPageNew'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TimerPage = lazy(() => import('./pages/TimerPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const RoomsPage = lazy(() => import('./pages/RoomsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const CoachPage = lazy(() => import('./pages/CoachPage'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const CommunityBlogPage = lazy(() => import('./pages/CommunityBlogPage'));
const SubjectsPage = lazy(() => import('./pages/SubjectsPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const NotesPage = lazy(() => import('./pages/NotesPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const WeeklyReviewPage = lazy(() => import('./pages/WeeklyReviewPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminControlLayer = lazy(() => import('./admin/AdminControlLayer'));
const AdminControlPage = lazy(() => import('./pages/AdminControlPage'));
const VoicePage = lazy(() => import('./pages/VoicePage'));
const ForestHistoryPage = lazy(() => import('./pages/ForestHistoryPage'));
const ForestEvolutionPage = lazy(() => import('./pages/ForestEvolutionPage'));
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'));

const RouteLoader = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-5 text-center">
      <p className="text-xs font-black uppercase tracking-[0.26em] text-[#94A3B8]">FocusFlow</p>
      <div className="mx-auto mt-4 h-10 w-10 animate-spin rounded-full border-2 border-[#8B5CF6]/30 border-t-[#8B5CF6]" />
      <p className="mt-4 text-sm text-[#CBD5E1]">Loading your study space...</p>
    </div>
  </div>
);

const App: React.FC = () => {
  const { initialize } = useAuthStore();

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    initialize().then((unsub) => {
      cleanup = unsub;
    });
    return () => cleanup?.();
  }, []);

  // Global theme initialization for public pages before AppLayout mounts
  useEffect(() => {
    if (localStorage.getItem('focusflow_theme') === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Protected app routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/timer" element={<TimerPage />} />
          <Route path="/rooms" element={<RoomsPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/coach" element={<CoachPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/blog" element={<CommunityBlogPage />} />
          <Route path="/subjects" element={<SubjectsPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/review" element={<WeeklyReviewPage />} />
          <Route path="/admin" element={<AdminControlLayer />} />
          <Route path="/admin/control" element={<AdminControlPage />} />
          <Route path="/admin/legacy" element={<AdminPage />} />
          <Route path="/voice" element={<VoicePage />} />
          <Route path="/forest-history" element={<ForestHistoryPage />} />
          <Route path="/evolution" element={<ForestEvolutionPage />} />
          <Route path="/achievements" element={<AchievementsPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
